import { LeftOutlined } from '@ant-design/icons';
import { Button, Col, Form, message, Row, Spin } from 'antd';
import { FormInstance } from 'antd/es/form/Form';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PinkBox from 'src/components/pink-box/pink-box';
import FileUploader from 'src/components/uploader/uploader';
import { MortgageDocumentTypeEnum } from 'src/constants/mortgage-deal';
import { scrollPageToTop } from 'src/helpers/window';
import useStore from 'src/hooks/useStore';
import { IMortgageSupportingDoc } from 'src/interfaces/mortgage-deal/supporting-document';
import { i18nKey } from 'src/locales/i18n';
import { IMortgageDealDetails } from 'src/store/mortgage-deal/mortgage-deal-details.store';

import styles from './mortgage-deal-supporting-doc.module.less';
import { DEFAULT_ALLOWED_OTHER_FILES } from 'src/constants';

interface IProps {
	onBack?: () => void;
	form: FormInstance;
	onSaveClick?: () => void;
	isViewFullForm?: boolean;
}

const MortgageDealSupportingDoc = ({ onBack, form, onSaveClick, isViewFullForm }: IProps) => {
	const [t] = useTranslation();

	const [loading, setLoading] = useState(false);

	const mortgageDetailsStore: IMortgageDealDetails = useStore('mortgageDealDetails');

	const onFileDelete = async (type: string, id: string | undefined) => {
		setLoading(true);
		const rs = await mortgageDetailsStore.deleteFile(id);
		if (rs) {
			message.success(t(i18nKey.feedback.fileDeleteSuccess));
			form.setFieldsValue({
				...mortgageDetailsStore.supportingDocuments
			});
		}
		setLoading(false);
	};

	const onFileChanged = async (type: number, file: File) => {
		setLoading(true);
		const form = new FormData();

		form.append('type', type.toString());
		form.append('file', file);
		const rs: boolean = await mortgageDetailsStore.uploadFile(form);
		setLoading(false);
		if (rs) {
			message.success(t(i18nKey.feedback.fileUploadSuccess));
		}
	};

	const onFileDownload = async (type: string, id: string | undefined) => {
		setLoading(true);
		await mortgageDetailsStore.downloadFile(type as keyof IMortgageSupportingDoc, id);
		setLoading(false);
	};

	const onSaveDraft = async () => {
		setLoading(true);

		const rs: boolean = await mortgageDetailsStore.saveDraft();

		if (rs) {
			onSaveClick && onSaveClick();
		}

		setLoading(false);
	};

	useEffect(() => {
		scrollPageToTop();
	}, []);

	useEffect(() => {
		form.setFieldsValue({
			...mortgageDetailsStore.supportingDocuments
		});
	}, [mortgageDetailsStore.supportingDocuments]);

	return (
		<>
			<PinkBox>
				<Spin spinning={loading}>
					<Form
						disabled={isViewFullForm}
						form={form}
						initialValues={{
							...mortgageDetailsStore.supportingDocuments
						}}>
						<div className={styles.container}>
							<Form.Item
								name="reservationOrMemorandumOfSaleDocument"
								rules={[
									{
										required: true,
										message: `${t(i18nKey.onboarding.validation.required)}`
									}
								]}>
								<FileUploader
									disabled={isViewFullForm}
									onChange={(files) => {
										onFileChanged(MortgageDocumentTypeEnum.reservationOrMemorandumOfSaleDocument, files);
									}}
									onDownload={onFileDownload}
									onDeleted={onFileDelete}
									typeId={'reservationOrMemorandumOfSaleDocument'}
									inputFiles={mortgageDetailsStore.supportingDocuments?.reservationOrMemorandumOfSaleDocument || []}
									title={`${t(i18nKey.mortgageDeal.requestForm.supportingDocument.reservationFormOrMemorandumOfSale)}`}
									requireUpload
								/>
							</Form.Item>
							<Form.Item
								name="contractOfExchangeDocument"
								rules={[
									{
										required: true,
										message: `${t(i18nKey.onboarding.validation.required)}`
									}
								]}>
								<FileUploader
									disabled={isViewFullForm}
									typeId={'contractOfExchangeDocument'}
									onDownload={onFileDownload}
									onDeleted={onFileDelete}
									inputFiles={mortgageDetailsStore.supportingDocuments?.contractOfExchangeDocument || []}
									title={`${t(i18nKey.onboarding.request.supportingDocument.contractOfExchange)}`}
									onChange={(files) => {
										onFileChanged(MortgageDocumentTypeEnum.contractOfExchangeDocument, files);
									}}
									requireUpload
								/>
							</Form.Item>
							<Form.Item
								name="titleFloorDocument"
								rules={[
									{
										required: true,
										message: `${t(i18nKey.onboarding.validation.required)}`
									}
								]}>
								<FileUploader
									disabled={isViewFullForm}
									typeId={'titleFloorDocument'}
									onDownload={onFileDownload}
									onDeleted={onFileDelete}
									inputFiles={mortgageDetailsStore.supportingDocuments?.titleFloorDocument || []}
									title={`${t(i18nKey.onboarding.request.supportingDocument.titleFloorPlan)}`}
									onChange={(files) => {
										onFileChanged(MortgageDocumentTypeEnum.titleFloorDocument, files);
									}}
									requireUpload
								/>
							</Form.Item>
							<Form.Item
								name="ukFinanceDocument"
								rules={[
									{
										required: true,
										message: `${t(i18nKey.onboarding.validation.required)}`
									}
								]}>
								<FileUploader
									disabled={isViewFullForm}
									typeId={'ukFinanceDocument'}
									onDownload={onFileDownload}
									onDeleted={onFileDelete}
									inputFiles={mortgageDetailsStore.supportingDocuments?.ukFinanceDocument || []}
									title={`${t(i18nKey.onboarding.request.supportingDocument.ukFinance)}`}
									onChange={(files) => {
										onFileChanged(MortgageDocumentTypeEnum.ukFinanceDocument, files);
									}}
									requireUpload
								/>
							</Form.Item>

							<Form.Item
								name="proofOfDepositDocument"
								rules={[
									{
										required: true,
										message: `${t(i18nKey.onboarding.validation.required)}`
									}
								]}>
								<FileUploader
									disabled={isViewFullForm}
									typeId={'proofOfDepositDocument'}
									onDownload={onFileDownload}
									onDeleted={onFileDelete}
									inputFiles={mortgageDetailsStore.supportingDocuments?.proofOfDepositDocument || []}
									title={`${t(i18nKey.onboarding.request.supportingDocument.proofOfDeposit)}`}
									onChange={(files) => {
										onFileChanged(MortgageDocumentTypeEnum.proofOfDepositDocument, files);
									}}
									requireUpload
								/>
							</Form.Item>

							<Form.Item
								name="surveyorReportDocument"
								rules={[
									{
										required: true,
										message: `${t(i18nKey.onboarding.validation.required)}`
									}
								]}>
								<FileUploader
									disabled={isViewFullForm}
									typeId={'surveyorReportDocument'}
									onDownload={onFileDownload}
									onDeleted={onFileDelete}
									inputFiles={mortgageDetailsStore.supportingDocuments?.surveyorReportDocument || []}
									title={`${t(i18nKey.onboarding.request.supportingDocument.surveyorReport)}`}
									onChange={(files) => {
										onFileChanged(MortgageDocumentTypeEnum.surveyorReportDocument, files);
									}}
									requireUpload
								/>
							</Form.Item>
							<Form.Item
								name="reportOnTitleOrTitleDeedDocument"
								rules={[
									{
										required: true,
										message: `${t(i18nKey.onboarding.validation.required)}`
									}
								]}>
								<FileUploader
									disabled={isViewFullForm}
									typeId={'reportOnTitleOrTitleDeedDocument'}
									onDownload={onFileDownload}
									onDeleted={onFileDelete}
									inputFiles={mortgageDetailsStore.supportingDocuments?.reportOnTitleOrTitleDeedDocument || []}
									title={`${t(i18nKey.mortgageDeal.requestForm.supportingDocument.reportOnTitleOrTitleDeed)}`}
									onChange={(files) => {
										onFileChanged(MortgageDocumentTypeEnum.reportOnTitleOrTitleDeedDocument, files);
									}}
									requireUpload
								/>
							</Form.Item>

							<Form.Item
								name="existingMortgageStatementRentRollDocument"
								rules={[
									{
										required: true,
										message: `${t(i18nKey.onboarding.validation.required)}`
									}
								]}>
								<FileUploader
									disabled={isViewFullForm}
									typeId={'existingMortgageStatementRentRollDocument'}
									onDownload={onFileDownload}
									onDeleted={onFileDelete}
									inputFiles={mortgageDetailsStore.supportingDocuments?.existingMortgageStatementRentRollDocument || []}
									title={`${t(i18nKey.mortgageDeal.requestForm.supportingDocument.ExistingMortgageStatementRentRoll)}`}
									onChange={(files) => {
										onFileChanged(MortgageDocumentTypeEnum.existingMortgageStatementRentRollDocument, files);
									}}
									requireUpload
								/>
							</Form.Item>

							<Form.Item
								name="existingTenancyDocument"
								rules={[
									{
										required: true,
										message: `${t(i18nKey.onboarding.validation.required)}`
									}
								]}>
								<FileUploader
									disabled={isViewFullForm}
									typeId={'existingTenancyDocument'}
									onDownload={onFileDownload}
									onDeleted={onFileDelete}
									inputFiles={mortgageDetailsStore.supportingDocuments?.existingTenancyDocument || []}
									title={`${t(i18nKey.onboarding.request.supportingDocument.existingTenancy)}`}
									onChange={(files) => {
										onFileChanged(MortgageDocumentTypeEnum.existingTenancyDocument, files);
									}}
									requireUpload
								/>
							</Form.Item>

							<Form.Item
								name="ews1FormDocument"
								rules={[
									{
										required: true,
										message: `${t(i18nKey.onboarding.validation.required)}`
									}
								]}>
								<FileUploader
									disabled={isViewFullForm}
									typeId={'ews1FormDocument'}
									onDownload={onFileDownload}
									onDeleted={onFileDelete}
									inputFiles={mortgageDetailsStore.supportingDocuments?.ews1FormDocument || []}
									title={`${t(i18nKey.onboarding.request.supportingDocument.ews1Form)}`}
									onChange={(files) => {
										onFileChanged(MortgageDocumentTypeEnum.ews1FormDocument, files);
									}}
									requireUpload
								/>
							</Form.Item>

							<Form.Item
								name={'valuationDocument'}
								rules={[
									{
										required: true,
										message: `${t(i18nKey.onboarding.validation.required)}`
									}
								]}>
								<FileUploader
									disabled={isViewFullForm}
									typeId={'valuationDocument'}
									onDownload={onFileDownload}
									onDeleted={onFileDelete}
									inputFiles={mortgageDetailsStore.supportingDocuments?.valuationDocument || []}
									title={`${t(i18nKey.mortgageDeal.requestForm.supportingDocument.ValuationReport)}`}
									onChange={(files) => {
										onFileChanged(MortgageDocumentTypeEnum.valuationDocument, files);
									}}
									requireUpload
								/>
							</Form.Item>
							<Form.Item
								name={'thirdPartyAgreementDocument'}
								rules={[
									{
										required: false,
										message: `${t(i18nKey.onboarding.validation.required)}`
									}
								]}>
								<FileUploader
									disabled={isViewFullForm}
									typeId={'thirdPartyAgreementDocument'}
									onDownload={onFileDownload}
									onDeleted={onFileDelete}
									inputFiles={mortgageDetailsStore.supportingDocuments?.thirdPartyAgreementDocument || []}
									title={`${t(i18nKey.mortgageDeal.requestForm.supportingDocument.ThirdPartyAgreement)}`}
									onChange={(files) => {
										onFileChanged(MortgageDocumentTypeEnum.thirdPartyAgreementDocument, files);
									}}
								/>
							</Form.Item>
							<Form.Item
								name={'othersDocument'}
								rules={[
									{
										required: false,
										message: `${t(i18nKey.onboarding.validation.required)}`
									}
								]}>
								<FileUploader
									disabled={isViewFullForm}
									typeId={'othersDocument'}
									onDownload={onFileDownload}
									onDeleted={onFileDelete}
									inputFiles={mortgageDetailsStore.supportingDocuments?.othersDocument || []}
									title={`${t(i18nKey.onboarding.request.supportingDocument.otherDocs)}`}
									onChange={(files) => {
										onFileChanged(MortgageDocumentTypeEnum.othersDocument, files);
									}}
									accept={DEFAULT_ALLOWED_OTHER_FILES}
								/>
							</Form.Item>
						</div>
					</Form>
				</Spin>
			</PinkBox>
			{!isViewFullForm && (
				<Row
					gutter={[
						{ xs: 0, sm: 0, md: 0, lg: 132 },
						{ xs: 20, sm: 20, md: 20, lg: 16 }
					]}
					style={{ marginTop: 30 }}>
					<Col span={24}>
						<Row gutter={16}>
							<Col xs={5} sm={5} md={5} lg={2}>
								<Button block type="primary" onClick={onBack}>
									<LeftOutlined />
								</Button>
							</Col>
							<Col xs={19} sm={19} md={19} lg={22}>
								<Button block type="primary" onClick={onSaveDraft}>
									{t(i18nKey.onboarding.saveAsDraft)}
								</Button>
							</Col>
						</Row>
					</Col>
				</Row>
			)}
		</>
	);
};

export default observer(MortgageDealSupportingDoc);
